import { ref } from 'vue';
import type { DataTableRowClickEvent } from 'primevue/datatable';
import { useRouter } from 'vue-router';

import type { FetchQueryParams } from '@/components/paginated-datatable/types/paginatedDataTableTypes';
import { useEmails } from '@/views/classification/hooks/useEmails';
import { useProcesses } from '@/views/processes/hooks/useProcesses';
import { emptyToUndefined } from '@/utils/object';
import {
  AddAttachmentToTicketV1Dto,
  AddEmailToTicketV1Dto,
  CreateTicketFromDocumentV1Dto,
  CreateTicketFromEMailV1Dto,
  EmailV1Dto,
  OrderByRelationalPropertyDto,
  type PatchTicketV1Dto,
  type TicketFilterInnerV2Dto,
  type TicketPointOfSaleV1Dto,
  TicketsService,
  TicketStatusAlert,
  type TicketV1Dto,
} from '@/openapi';
import { eventBus } from '@/configs/eventBus';
import { type ProcessSlipEntity, type ProcessSlipObject, toSlipObject } from '@/types/process-slip';

import type {
  AssignTicketParams,
  CreateTicketParams,
  TicketCustomerData,
  TicketEmailData,
  TicketEnriched,
  TicketSimple,
} from '../ticketsTypes';

export function useTickets() {
  const router = useRouter();
  const isLoading = ref<boolean>(false);

  const fetchTicket = async (id: string) => {
    try {
      isLoading.value = true;
      return await TicketsService.ticketControllerV1FindOneV1(id);
    } catch (err) {
      console.log(err);
    } finally {
      isLoading.value = false;
    }
  };

  const fetchStepForTicket = async (id: string) => {
    try {
      return await TicketsService.processTicketControllerV1FindCurrentStepV1(id);
    } catch (err) {
      console.log(err);
    }
  };

  const fetchTicketTeams = async (): Promise<string[]> => {
    try {
      return await TicketsService.ticketControllerV2GetAllTeamNamesV2();
    } catch (err) {
      console.log(err);
      return [];
    }
  };

  const transitionStepForTicket = async (ticketId: string, nextStepId: string) => {
    try {
      return await TicketsService.processTicketControllerV1TransitionStepV1(ticketId, {
        nextStep: nextStepId,
      });
    } catch (err) {
      console.log(err);
    }
  };

  const fetchTickets =
    ({
      orderBy,
      page,
      limit,
      filter,
      searchString,
      statusAlert,
    }: FetchQueryParams<TicketFilterInnerV2Dto> & {
      statusAlert?: TicketStatusAlert;
    }) =>
    async () => {
      try {
        return TicketsService.ticketControllerV2FindAllV2(
          orderBy,
          page,
          limit,
          filter,
          searchString,
          statusAlert,
        );
      } catch (err) {
        console.error(err);
      }
    };

  const fetchTicketsSimple = (
    orderBy?: Array<OrderByRelationalPropertyDto> | null,
    searchString?: string | null,
    page?: number,
    limit?: number,
  ) => {
    try {
      return TicketsService.ticketControllerV2FindAllSimplifiedV2(
        orderBy,
        page,
        limit,
        searchString,
      );
    } catch (err) {
      console.error(err);
    }
  };

  // ticketControllerPatchTicktet
  const updateTicket = async (id: string, data: PatchTicketV1Dto, silent?: boolean) => {
    try {
      const response = await TicketsService.ticketControllerV1UpdatePartialV1(id, data);
      eventBus.emit(
        'update:ticket',
        { ticketId: id, ticket: response, silent },
        { bubble: !silent },
      );

      return response;
    } catch (err) {
      console.log(err);
    }
  };

  const updateTicketNotification = async (ticketId: string, value: boolean) => {
    try {
      if (value) {
        await TicketsService.ticketNotificationControllerV1MarkNotificationsAsUnreadByUserV1(
          ticketId,
        );
      } else {
        await TicketsService.ticketNotificationControllerV1MarkNotificationsAsReadV1(ticketId, {});
      }
    } catch (err) {
      console.log(err);
    }
  };

  // ticketControllerRemove
  const deleteTicket = async (id: string) => {
    try {
      await TicketsService.ticketControllerV1RemoveOneV1(id);
      eventBus.emit('delete');
    } catch (err) {
      console.log(err);
    }
  };

  // ticketControllerRemoveBatch
  const deleteTickets = async (ids: string[]) => {
    try {
      await TicketsService.ticketControllerV1RemoveManyV1({ values: ids });
      eventBus.emit('delete');
    } catch (err) {
      console.log(err);
    }
  };

  // ticketControllerCreateFromEmail
  const createTicketFromEmail = async (emailId: string, data: CreateTicketFromEMailV1Dto) => {
    try {
      await TicketsService.ticketControllerV1CreateFromEmailV1(emailId, data);
      eventBus.emit('create');
    } catch (err) {
      console.log(err);
    }
  };

  // ticketControllerAddEmailToTicket
  const addEmailToTicket = async (
    ticketId: string,
    emailId: string,
    data: AddEmailToTicketV1Dto,
  ) => {
    try {
      await TicketsService.ticketControllerV1AddEmailToTicketV1(ticketId, emailId, data);
      eventBus.emit('update');
    } catch (err) {
      console.log(err);
    }
  };

  // ticketControllerCreateFromAttachment
  const createTicketFromAttachment = async (
    attachmentId: string,
    data: CreateTicketFromDocumentV1Dto,
  ) => {
    try {
      await TicketsService.ticketControllerV1CreateFromAttachmentV1(attachmentId, data);
      eventBus.emit('create');
    } catch (err) {
      console.log(err);
    }
  };

  const addAttachmentToTicket = async (
    ticketId: string,
    attachmentId: string,
    data: AddAttachmentToTicketV1Dto,
  ) => {
    try {
      await TicketsService.ticketControllerV1AddAttachmentToTicketV1(ticketId, attachmentId, data);
      eventBus.emit('update');
    } catch (err) {
      console.log(err);
    }
  };

  const handleCreateTicketClick = async ({
    emailId,
    attachmentId,
    pointOfSaleId,
    documentType,
    selectedMailIncludeAttachments,
    onFinally,
  }: CreateTicketParams) => {
    try {
      if (!emailId || !pointOfSaleId || !documentType) return;

      if (attachmentId) {
        const createFromAttachmentData: CreateTicketFromDocumentV1Dto = {
          pointOfSaleId: pointOfSaleId,
          documentType: documentType as CreateTicketFromDocumentV1Dto.documentType,
        };
        await createTicketFromAttachment(attachmentId, createFromAttachmentData);
      } else {
        const createTicketFromEmailData: CreateTicketFromEMailV1Dto = {
          pointOfSaleId: pointOfSaleId,
          documentType: documentType as CreateTicketFromEMailV1Dto.documentType,
          includeAllAttachments: selectedMailIncludeAttachments,
        };
        await createTicketFromEmail(emailId, createTicketFromEmailData);
      }
    } finally {
      await onFinally();
    }
  };

  const handleAssignTicketClick = async ({
    ticketId,
    emailId,
    attachmentId,
    pointOfSaleId,
    documentType,
    selectedMailIncludeAttachments,
    onFinally,
  }: AssignTicketParams) => {
    try {
      if (!ticketId || !emailId || !documentType || !pointOfSaleId) return;

      if (attachmentId) {
        const attachmentData: AddAttachmentToTicketV1Dto = {
          pointOfSaleId: pointOfSaleId,
          documentType: documentType as AddAttachmentToTicketV1Dto.documentType,
        };
        await addAttachmentToTicket(ticketId, attachmentId, attachmentData);
      } else {
        const emailToTicketData: AddEmailToTicketV1Dto = {
          pointOfSaleId: pointOfSaleId,
          documentType: documentType as AddEmailToTicketV1Dto.documentType,
          includeAllAttachments: selectedMailIncludeAttachments,
        };
        await addEmailToTicket(ticketId, emailId, emailToTicketData);
      }
    } finally {
      await onFinally();
    }
  };

  function goToTicketByNumber(event: { data: DataTableRowClickEvent['data'] }) {
    const number = event.data?.number?.meta?.('rawvalue');
    if (!number) return;

    void router.push(`/tickets/${number}`);
  }

  function goToTicketByIdFromRow(event: { data: DataTableRowClickEvent['data'] }) {
    const id = event.data?.id?.meta?.('rawvalue');
    if (!id) return;

    void router.push(`/tickets/${id}`);
  }

  const goToTicketById = (ticketID: string) => {
    eventBus.emit('goto', { path: `/tickets/${ticketID}` });
  };

  async function saveEntityToProcessSlip({
    ticketId,
    version,
    processSlip,
    entryKey,
    entryValue,
  }: ProcessSlipEntity) {
    try {
      // Always normalize to an object
      const parsed: ProcessSlipObject = toSlipObject(processSlip) ?? {};

      // Deep-ish merge for uiPluginStates; overwrite target key otherwise
      const updatedSlip: ProcessSlipObject = (
        entryKey === 'uiPluginStates'
          ? {
              ...parsed,
              uiPluginStates: {
                ...(parsed.uiPluginStates ?? {}),
                ...(entryValue as Record<string, any>), // merge atomic patch into the map
              },
            }
          : { ...parsed, [entryKey]: entryValue as any }
      ) as ProcessSlipObject;

      // Send object (NOT a JSON string)
      const response = await updateTicket(
        ticketId,
        {
          processSlip: updatedSlip,
          version,
        },
        true,
      );

      return response;
    } catch (err) {
      eventBus.emit('error500', { message: `brokenJSON` });
      console.error(`Fehler beim Speichern von ${entryKey} im processSlip:`, err);
    }
  }

  const { fetchProcess, extractProcessNameFromProcdefUrl, extractProcessStepIdFromProcdefUrl } =
    useProcesses();

  async function useEnrichedTicket(idOrUid: string): Promise<TicketEnriched | undefined> {
    const { fetchTicket, fetchStepForTicket } = useTickets();
    const { fetchEmailsByTicket } = useEmails();
    const { convertTicketDtoToCustomerData, convertEmailsToTicketEmailData } =
      useTicketsConverter();

    const ticketResponse = await fetchTicket(idOrUid);
    if (!ticketResponse) return undefined;

    const processID = extractProcessNameFromProcdefUrl(String(ticketResponse.procdefUrl)) ?? '';
    const process = await fetchProcess(processID);
    const processStepID =
      extractProcessStepIdFromProcdefUrl(String(ticketResponse.procdefUrl)) ?? process.start;

    // Get static step definition from process library
    const staticStep = process.steps.find((step) => step.id === processStepID);

    // Always get dynamic data (history, options) from backend
    const dynamicStep = await fetchStepForTicket(ticketResponse.id);

    // Merge: use static definition with dynamic history & options
    const currentStep = {
      ...staticStep,
      ...dynamicStep,
      // Preserve static step definition properties if they exist
      id: staticStep?.id ?? dynamicStep?.id,
      name: staticStep?.name ?? dynamicStep?.name,
    };

    const emails = convertEmailsToTicketEmailData(
      (await fetchEmailsByTicket(ticketResponse.id)) ?? [],
    );

    const enrichedTicket: TicketEnriched = {
      ...ticketResponse,
      processStep: currentStep as unknown as TicketEnriched['processStep'],
      emails,
      team: ticketResponse.team ?? undefined,
      stepDeadlinedAt: ticketResponse.stepDeadlinedAt ?? undefined,
      processDeadlinedAt: ticketResponse.processDeadlinedAt ?? undefined,
      pointOfSaleConverted: convertTicketDtoToCustomerData(ticketResponse.pointOfSale),
    };

    return enrichedTicket;
  }

  return {
    fetchTicket,
    fetchStepForTicket,
    fetchTicketTeams,
    transitionStepForTicket,
    fetchTickets,
    fetchTicketsSimple,
    updateTicket,
    updateTicketNotification,
    deleteTicket,
    deleteTickets,
    createTicketFromEmail,
    addEmailToTicket,
    createTicketFromAttachment,
    addAttachmentToTicket,
    handleCreateTicketClick,
    handleAssignTicketClick,
    goToTicketByNumber,
    useEnrichedTicket,
    saveEntityToProcessSlip,
    isLoading,
    goToTicketById,
    goToTicketByIdFromRow,
  };
}

export function useTicketsConverter() {
  const convertTicketDtoToTicketSimple = (data: TicketV1Dto): TicketSimple => {
    return {
      ...data,
    };
  };

  const convertTicketSimpleToUpdateTicketto = (data: TicketSimple): PatchTicketV1Dto => {
    // Remove fields that are not part of PatchTicketDto or need adaptation
    const { id: _id, status: _status, processSlip, ...rest } = data;

    // Normalize slip to object or undefined
    const slipObj: ProcessSlipObject = emptyToUndefined(
      toSlipObject(processSlip),
    ) as ProcessSlipObject;

    // Rebuild PatchTicketDto with sanitized processSlip
    const patch: PatchTicketV1Dto = {
      ...rest,
      ...(slipObj ? { processSlip: slipObj } : {}),
      version: rest.version,
    } as PatchTicketV1Dto;

    return patch;
  };

  const convertTicketDtoToCustomerData = (
    pointOfSale: TicketPointOfSaleV1Dto | undefined,
  ): TicketCustomerData => {
    return {
      ...pointOfSale,
      posId: pointOfSale?.id,
      posName: pointOfSale?.name,
      customerId: pointOfSale?.customer?.id,
      customerName: pointOfSale?.customer?.name,
      customerCountry: pointOfSale?.customer?.country,
      customerGroupId: pointOfSale?.customer.customerGroup?.id,
      customerGroup: pointOfSale?.customer.customerGroup?.name,
      customer: undefined,
      name: undefined,
    };
  };

  const convertEmailsToTicketEmailData = (emails: EmailV1Dto[]): TicketEmailData[] => {
    return emails.map((email) => ({
      ...email,
      documentType: email.tickets[0]?.documentType ?? 'none',
    }));
  };

  return {
    convertTicketDtoToTicketSimple,
    convertTicketSimpleToUpdateTicketto,
    convertTicketDtoToCustomerData,
    convertEmailsToTicketEmailData,
  };
}
